import { MGPFallible, MGPOptional, MGPValidation, Utils } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { GameStatus } from '../../jscaip/GameStatus';
import { HexagonalUtils } from '../../jscaip/HexagonalUtils';
import { Player } from '../../jscaip/Player';
import { PlayerNumberMap } from '../../jscaip/PlayerMap';
import { Rules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { DvonnFailure } from './DvonnFailure';
import { DvonnMove } from './DvonnMove';
import { DvonnPieceStack } from './DvonnPieceStack';
import { DvonnState } from './DvonnState';
export class DvonnNode extends GameNode {
}
export class DvonnRules extends Rules {
    static singleton = MGPOptional.empty();
    static get() {
        if (DvonnRules.singleton.isAbsent()) {
            DvonnRules.singleton = MGPOptional.of(new DvonnRules());
        }
        return DvonnRules.singleton.get();
    }
    getInitialState() {
        return new DvonnState(DvonnState.balancedBoard(), 0, false);
    }
    static getGameStatus(node) {
        const state = node.gameState;
        const scores = DvonnRules.getScores(state);
        if (DvonnRules.getMovablePieces(state).length === 0) {
            // This is the end of the game, boost the score to clearly indicate it
            const scoresZero = scores.get(Player.ZERO);
            const scoresOne = scores.get(Player.ONE);
            if (scoresZero > scoresOne) {
                return GameStatus.ZERO_WON;
            }
            else if (scoresZero < scoresOne) {
                return GameStatus.ONE_WON;
            }
            else {
                return GameStatus.DRAW;
            }
        }
        else {
            return GameStatus.ONGOING;
        }
    }
    static getMovablePieces(state) {
        // Movable pieces are the one that are free
        // and which can move to target (an occupied space at a distance equal to their length)
        return DvonnRules.getFreePieces(state)
            .filter((c) => DvonnRules.pieceHasTarget(state, c));
    }
    static getFreePieces(state) {
        // Free pieces are the ones that have less than 6 neighbors (and belong to the current player)
        return state.getAllPieces()
            .filter((c) => state.getPieceAt(c).belongsTo(state.getCurrentPlayer()) &&
            state.numberOfNeighbors(c) < 6);
    }
    static pieceHasTarget(state, coord) {
        // A piece has a target if it can move to an occupied space at a distance equal to its length
        const stackSize = state.getPieceAt(coord).getSize();
        const possibleTargets = HexagonalUtils.getNeighbors(coord, stackSize);
        return possibleTargets.find((c) => state.coordHasPieces(c)) !== undefined;
    }
    static pieceTargets(state, coord) {
        const stackSize = state.getPieceAt(coord).getSize();
        const possibleTargets = HexagonalUtils.getNeighbors(coord, stackSize);
        return possibleTargets.filter((c) => state.coordHasPieces(c));
    }
    static getScores(state) {
        // Board value is the total number of pieces controlled by player 0 - by player 1
        let p0Score = 0;
        let p1Score = 0;
        state.getAllPieces().map((c) => {
            const stack = state.getPieceAt(c);
            if (stack.belongsTo(Player.ZERO)) {
                p0Score += stack.getSize();
            }
            else if (stack.belongsTo(Player.ONE)) {
                p1Score += stack.getSize();
            }
        });
        return PlayerNumberMap.of(p0Score, p1Score);
    }
    isMovablePiece(state, coord) {
        Utils.assert(state.isOnBoard(coord), 'piece is not on the board');
        const stack = state.getPieceAt(coord);
        if (stack.getSize() < 1) {
            return MGPValidation.failure(DvonnFailure.EMPTY_STACK());
        }
        if (stack.belongsTo(state.getCurrentPlayer()) === false) {
            return MGPValidation.failure(DvonnFailure.NOT_PLAYER_PIECE());
        }
        if (6 <= state.numberOfNeighbors(coord)) {
            return MGPValidation.failure(DvonnFailure.TOO_MANY_NEIGHBORS());
        }
        if (DvonnRules.pieceHasTarget(state, coord) === false) {
            return MGPValidation.failure(DvonnFailure.CANT_REACH_TARGET());
        }
        return MGPValidation.SUCCESS;
    }
    canOnlyPass(state) {
        return DvonnRules.getMovablePieces(state).length === 0;
    }
    sourceCoords(state) {
        return state.getAllPieces()
            .filter((c) => state.getPieceAt(c).containsSource());
    }
    markPiecesConnectedTo(state, coord, markBoard) {
        // For each neighbor, mark it as connected (if it contains something),
        // and recurse from there (only if it was not already marked)
        HexagonalUtils.getNeighbors(coord, 1).forEach((c) => {
            if (state.coordHasPieces(c) && markBoard[c.y][c.x] === false) {
                // This piece has not been marked as connected, but it is connected, and not empty
                markBoard[c.y][c.x] = true; // mark it as connected
                this.markPiecesConnectedTo(state, c, markBoard); // find all pieces connected to this one
            }
        });
    }
    removeDisconnectedPieces(state) {
        // This will contain true for each piece connected to a source
        const markBoard = TableUtils.create(DvonnState.WIDTH, DvonnState.HEIGHT, false);
        this.sourceCoords(state).forEach((c) => {
            markBoard[c.y][c.x] = true; // marks the source as true in the markBoard
            this.markPiecesConnectedTo(state, c, markBoard);
        });
        let newState = state;
        newState.getAllPieces().forEach((c) => {
            if (markBoard[c.y][c.x] === false) {
                newState = newState.setAt(c, DvonnPieceStack.EMPTY);
            }
        });
        return newState;
    }
    applyLegalMove(move, state, _config, _info) {
        if (move === DvonnMove.PASS) {
            return new DvonnState(state.board, state.turn + 1, true);
        }
        else {
            // To apply a legal move, the stack is added in the front of its end coordinate
            // (and removed from its start coordinate)
            const stack = state.getPieceAt(move.getStart());
            const targetStack = state.getPieceAt(move.getEnd());
            const newState = state
                .setAt(move.getStart(), DvonnPieceStack.EMPTY)
                .setAt(move.getEnd(), DvonnPieceStack.append(stack, targetStack));
            const resultingState = this.removeDisconnectedPieces(new DvonnState(newState.board, state.turn + 1, false));
            return resultingState;
        }
    }
    isLegal(move, state, _config) {
        if (DvonnRules.getMovablePieces(state).length === 0) {
            // If no pieces are movable, the player can pass
            // but only if the previous move was not a pass itself
            if (move === DvonnMove.PASS && state.alreadyPassed === false) {
                return MGPValidation.SUCCESS;
            }
            else {
                return MGPValidation.failure(RulesFailure.MUST_PASS());
            }
        }
        else if (move === DvonnMove.PASS) {
            return MGPValidation.failure(RulesFailure.CANNOT_PASS());
        }
        const pieceMovable = this.isMovablePiece(state, move.getStart());
        if (pieceMovable.isFailure()) {
            return pieceMovable;
        }
        const stack = state.getPieceAt(move.getStart());
        if (move.getDistance() !== stack.getSize()) {
            return MGPFallible.failure(DvonnFailure.INVALID_MOVE_LENGTH());
        }
        const targetStack = state.getPieceAt(move.getEnd());
        if (targetStack.isEmpty()) {
            return MGPFallible.failure(DvonnFailure.EMPTY_TARGET_STACK());
        }
        return MGPFallible.success(undefined);
    }
    getGameStatus(node) {
        return DvonnRules.getGameStatus(node);
    }
}
